import { computed, inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

export interface OrderTrackingState {
  enabled: boolean;
  text: string;
  tokens: string[];
}

export interface OrderStopRecord {
  id: number;
  stopType: number;
  tripId: number;
  address: string;
  comment: string;
  latitude: number | null;
  longitude: number | null;
  status: boolean;
  seq: number;
}

export interface OrderRecord {
  orderId: string;
  statusId: number | null;
  userId: string | null;
  driverId: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  driverFirstName: string | null;
  driverLastName: string | null;
  userMobile: string | null;
  driverMobile: string | null;
  driverName: string;
  carManufacturer: string | null;
  carNumber: string | null;
  carColor: string | null;
  regionId: number | null;
  tariffId: number | null;
  paymentMethodId: number | null;
  acceptedDate: string | null;
  cancelDate: string | null;
  completedDate: string | null;
  createdDate: string | null;
  waitingDate: string | null;
  processingDate: string | null;
  address: string;
  comment: string;
  destinationAddress: string;
  destinationComment: string;
  destinationLatitude: number | null;
  destinationLongitude: number | null;
  latitude: number | null;
  longitude: number | null;
  carTypeId: number | null;
  carTypeDescription: string;
  corporate: boolean;
  business: boolean;
  surgePrice: number | null;
  options: number[];
  processing: boolean;
  tracking: boolean;
  orderDuplicate: boolean;
  ordersInSixMonths: number | null;
  totalOrders: number | null;
  ordersInLastMonth: number | null;
  cancelCount: number;
  fareEstimate: number | null;
  hint: string | null;
  merchantOrderId: string | null;
  preOrderId: string | null;
  preOrderPaymentTypeName: string | null;
  deliveryDate: string | null;
  businessDeliveryStatus: number | null;
  orderStops: OrderStopRecord[];
  sourcePlatform: string | null;
  sourceAppVersion: string | null;
}

export interface OrdersStateModel {
  byId: Record<string, OrderRecord>;
  pendingIds: string[];
  deliveryIds: string[];
  activeIds: string[];
  allIds: string[];
  balanceIds: string[];
  selectedOrderId: string | null;
  tracking: OrderTrackingState;
  updatedAt: string | null;
}

const ORDERS_STATE_DEFAULTS: OrdersStateModel = {
  byId: {},
  pendingIds: [],
  deliveryIds: [],
  activeIds: [],
  allIds: [],
  balanceIds: [],
  selectedOrderId: null,
  tracking: {
    enabled: false,
    text: '',
    tokens: []
  },
  updatedAt: null
};

export class ReplaceOrdersSnapshot {
  static readonly type = '[Orders] Replace Snapshot';

  constructor(public readonly payload: Partial<OrdersStateModel>) {}
}

export class SelectOrder {
  static readonly type = '[Orders] Select';

  constructor(public readonly orderId: string | null) {}
}

export class SetOrderTrackingState {
  static readonly type = '[Orders] Set Tracking State';

  constructor(public readonly payload: OrderTrackingState) {}
}

export class ClearOrdersState {
  static readonly type = '[Orders] Clear';
}

@State<OrdersStateModel>({
  name: 'orders',
  defaults: ORDERS_STATE_DEFAULTS
})
@Injectable()
export class OrdersState {
  @Selector()
  static snapshot(state: OrdersStateModel): OrdersStateModel {
    return state;
  }

  @Selector()
  static selectedOrderId(state: OrdersStateModel): string | null {
    return state.selectedOrderId;
  }

  @Selector()
  static tracking(state: OrdersStateModel): OrderTrackingState {
    return state.tracking;
  }

  @Selector()
  static queueCounts(state: OrdersStateModel): Record<string, number> {
    return {
      pending: state.pendingIds.length,
      delivery: state.deliveryIds.length,
      active: state.activeIds.length,
      all: state.allIds.length,
      balance: state.balanceIds.length
    };
  }

  @Action(ReplaceOrdersSnapshot)
  replaceSnapshot(ctx: StateContext<OrdersStateModel>, action: ReplaceOrdersSnapshot): void {
    ctx.patchState({
      ...action.payload,
      updatedAt: action.payload.updatedAt ?? new Date().toISOString()
    });
  }

  @Action(SelectOrder)
  selectOrder(ctx: StateContext<OrdersStateModel>, action: SelectOrder): void {
    ctx.patchState({
      selectedOrderId: action.orderId
    });
  }

  @Action(SetOrderTrackingState)
  setOrderTrackingState(
    ctx: StateContext<OrdersStateModel>,
    action: SetOrderTrackingState
  ): void {
    ctx.patchState({
      tracking: action.payload
    });
  }

  @Action(ClearOrdersState)
  clearOrdersState(ctx: StateContext<OrdersStateModel>): void {
    ctx.setState(ORDERS_STATE_DEFAULTS);
  }
}

@Injectable({ providedIn: 'root' })
export class OrdersFacade {
  private readonly store = inject(Store);

  readonly orders = this.store.selectSignal(OrdersState.snapshot);
  readonly selectedOrderId = this.store.selectSignal(OrdersState.selectedOrderId);
  readonly tracking = this.store.selectSignal(OrdersState.tracking);
  readonly queueCounts = this.store.selectSignal(OrdersState.queueCounts);
  readonly selectedOrder = computed<OrderRecord | null>(() => {
    const selectedOrderId = this.selectedOrderId();
    const state = this.orders();

    if (!selectedOrderId) {
      return null;
    }

    return state.byId[selectedOrderId] ?? null;
  });

  replaceSnapshot(payload: Partial<OrdersStateModel>): void {
    this.store.dispatch(new ReplaceOrdersSnapshot(payload));
  }

  selectOrder(orderId: string | null): void {
    this.store.dispatch(new SelectOrder(orderId));
  }

  setTrackingState(payload: OrderTrackingState): void {
    this.store.dispatch(new SetOrderTrackingState(payload));
  }

  clear(): void {
    this.store.dispatch(new ClearOrdersState());
  }
}
